import { Box, Divider, Typography } from "@material-ui/core";
import React, { useState, useCallback, useMemo, useContext } from "react";
import {
  ActionLink,
  BannerSeverity,
  CurrencyFormatters,
  getTotalPriceText,
  IconName,
  Icon,
  NotificationBanner,
  twoDecimalFormatter,
} from "halifax";
import {
  Airline,
  getDepartureSlice,
  getReturnSlice,
  PackageItinerary,
} from "redmond";
import dayjs from "dayjs";
import clsx from "clsx";
import { getConfigTenant, isCorpTenant } from "@capone/common";
import { config } from "b2b-base/src/api/config";
import * as textConstants from "./constants";
import "./styles.scss";
import { ClientContext } from "../../../../../../../../App";
import { getFlightItineraryDetails } from "./constants";
import { formatPaymentBreakdown, ILineItem } from "../../../FlightCard/helpers";

interface IPackageItemizedReceiptModalContentProps {
  packageItinerary: PackageItinerary;
  documentId?: string;
  isMobile?: boolean;
  airlineMap: { [key: string]: Airline };
}

export const PackageItemizedReceiptModalContent = ({
  packageItinerary,
  documentId = textConstants.DEFAULT_DOC_ID,
  isMobile,
  airlineMap,
}: IPackageItemizedReceiptModalContentProps) => {
  const { logo } = useContext(ClientContext);
  const { pricingBreakdown, hotel, flight, paymentBreakdown } =
    packageItinerary;
  const { reservation } = hotel.itinerary;
  const {
    guests,
    lodgingData,
    reservationId,
    createdAt,
    checkInDate,
    checkOutDate,
  } = reservation;
  const { bookedItinerary } = flight.itinerary;

  const isCorporate = isCorpTenant(getConfigTenant());

  const [printing, setPrinting] = useState(false);

  const guestName = `${guests[0].givenName} ${guests[0].surname}`;
  const resId = `H-${reservationId}`;
  const addCurrency = (num: number) =>
    `${CurrencyFormatters.get(
      pricingBreakdown.subtotal.totalAmount.fiat.currencyCode
    ).format(Math.abs(num))}`;
  const invoiceDate = dayjs(createdAt, "YYYY-MM-DDTHH:mm:ss.SSS").format(
    textConstants.MMDDYYYY_FORMAT
  );

  const flightDetails = getFlightItineraryDetails(
    bookedItinerary.travelItinerary.locators,
    getDepartureSlice(bookedItinerary),
    getReturnSlice(bookedItinerary),
    airlineMap
  );

  const lineItems =
    paymentBreakdown && formatPaymentBreakdown(paymentBreakdown);

  const handlePrint = useCallback(() => {
    setPrinting(true);

    setTimeout(() => {
      const divContents = document.getElementById(documentId)?.innerHTML;
      const cssHeadContents = document.head?.getElementsByTagName("style");
      const cssBodyContents = document.body.getElementsByTagName("style");
      const cssBodyStyle = document.body.getAttribute("style");
      const a = window.open(" ", " ", "height=800, width=400");
      const doc = a?.document;
      if (doc) {
        doc.write("<html><head>");
        for (let i = 0; i < cssHeadContents.length; i++) {
          doc.write(
            `<style>${cssHeadContents.item(i)?.innerHTML || ""}</style>`
          );
        }

        doc.write(`</head><body style="${cssBodyStyle}" >${divContents || ""}`);

        for (let i = 0; i < cssBodyContents.length; i++) {
          doc.write(
            `<style>${cssBodyContents.item(i)?.innerHTML || ""}</style>`
          );
        }
        doc.write("</body></html>");
        if (a)
          a.document.title = `${guestName} - ${resId} ${textConstants.TITLE}`;
        setTimeout(() => {
          a?.print();
          doc.close();
          setPrinting(false);
        }, 500);
      }
    }, 10);
  }, []);

  const renderRewards = useMemo(() => {
    const rewards = lineItems?.rewards;
    if (!rewards) {
      return null;
    }
    return (
      <Box className="line-item">
        <Typography variant="body1" className="green">
          {rewards.label}
        </Typography>
        <Typography variant="body1" className="rewards-points bold green">
          {rewards.amount}
        </Typography>
      </Box>
    );
  }, [lineItems]);

  const renderPayment = useMemo(() => {
    const userCard = lineItems?.userCard;
    if (!userCard) {
      return null;
    }
    return (
      <Box className="line-item">
        <Typography variant="body1" className="payment-card-details">
          {`Card ending in ${userCard.label?.slice(-4)}`}
        </Typography>

        <Typography variant="body1" className="payment-card-amount bold">
          {userCard.amount}
        </Typography>
      </Box>
    );
  }, [lineItems]);

  const renderEarnBanner = useMemo(() => {
    if (hotel.itinerary.redeemedEarnOffers) {
      return hotel.itinerary.redeemedEarnOffers.map((offer) => (
        <NotificationBanner
          className="offer-earn-banner"
          strongLabel={offer.description}
          severity={BannerSeverity.IMPROVEMENT}
          icon={
            <Icon
              name={IconName.CheckCircleFilledGreen}
            />
          }
        />
      ));
    }
    return null;
  }, [hotel.itinerary.redeemedEarnOffers]);

  const renderTravelWallet = (travelWallet: ILineItem | null) => {
    if (!travelWallet) return null;
    return (
      <Box className="line-item">
        <Typography variant="body1" className="green">
          {travelWallet.label}
        </Typography>
        <Typography variant="body1" className="offer-amount bold green">
          {travelWallet.amount}
        </Typography>
      </Box>
    );
  };

  const renderOffers = useMemo(() =>
    lineItems && renderTravelWallet(lineItems?.travelWalletOffer),
    [lineItems]);

  const renderCredits = useMemo(() =>
    lineItems?.travelWalletCredit?.map((credit) => renderTravelWallet(credit)),
    [lineItems]);

  const renderPrintButton = useMemo(() => {
    if (printing) return null;

    return isMobile ? (
      <></>
    ) : (
      <ActionLink
        className="print-btn"
        content={textConstants.PRINT}
        onClick={handlePrint}
      />
    );
  }, [isMobile, printing]);

  return (
    <Box className="itemized-receipt-modal-container" id={documentId}>
      <Box className="itemized-package-receipt-modal-content">
        <Box className="itemized-receipt-logo-container">
          {logo}
          {renderPrintButton}
        </Box>
        {/* hotel section */}
        <Box className="itemized-receipt-title-container">
          <Typography variant="h2">{textConstants.TITLE}</Typography>
        </Box>
        <Box className="itemized-receipt-details">
          <Box className="line-item">
            <Typography variant="body1">{textConstants.HOTEL_NAME}:</Typography>
            <Typography variant="body1" className="hotel-name bold">
              {lodgingData.name}
            </Typography>
          </Box>
          <Box className="line-item">
            <Typography variant="body1">{textConstants.GUEST_NAME}:</Typography>
            <Typography variant="body1" className="bold">
              {guestName}
            </Typography>
          </Box>
          <Box className="line-item gutter-bottom">
            <Typography variant="body1">
              {textConstants.CONFIRMATION_NUMBER}:
            </Typography>
            <Typography variant="body1" className="bold green">
              {resId}
            </Typography>
          </Box>
          <Divider className="line-item gutter-bottom" />
          {/* flight section */}
          <Box className="line-item">
            <Typography variant="body1">
              {textConstants.OUTBOUND_AIRLINE}
            </Typography>
            <Typography variant="body1" className="hotel-name bold">
              {flightDetails.outboundAirlineName}
            </Typography>
          </Box>
          <Box className="line-item">
            <Typography variant="body1">
              {textConstants.RETURN_AIRLINE}:
            </Typography>
            <Typography variant="body1" className="hotel-name bold">
              {flightDetails.returnAirlineName}
            </Typography>
          </Box>
          <Box className="line-item">
            <Typography variant="body1">{textConstants.TRAVELERS}:</Typography>
            <Box>
              {bookedItinerary.passengers.alone.map((pax) => (
                <Typography className="bold right-align" key={pax.person.id}>
                  {pax.person.givenName} {pax.person.surname}
                </Typography>
              ))}
              {bookedItinerary.passengers.withLapInfants.map((pax) => (
                <React.Fragment key={pax.adult.person.id}>
                  <Typography className="bold right-align">
                    {pax.adult.person.givenName} {pax.adult.person.surname}
                  </Typography>
                  <Typography className="bold right-align">
                    {pax.infant.person.givenName} {pax.infant.person.surname}
                  </Typography>
                </React.Fragment>
              ))}
            </Box>
          </Box>

          <Box className="line-item gutter-bottom">
            <Typography variant="body1">
              {textConstants.CONFIRMATION_NUMBER}:
            </Typography>
            <Typography variant="body1" className="bold green">
              {flightDetails.confirmationCode}
            </Typography>
          </Box>
          <Divider className="line-item gutter-bottom" />

          <Box className="line-item">
            <Typography variant="body1">
              {textConstants.INVOICE_DATE}:
            </Typography>
            <Typography variant="body1" className="bold">
              {invoiceDate}
            </Typography>
          </Box>
          <Box className="line-item">
            <Typography variant="body1">{textConstants.CHECK_IN}:</Typography>
            <Typography variant="body1" className="bold">
              {dayjs(checkInDate).format(textConstants.MMDDYYYY_FORMAT)}
            </Typography>
          </Box>
          <Box className="line-item gutter-bottom">
            <Typography variant="body1">{textConstants.CHECKOUT}:</Typography>
            <Typography variant="body1" className="bold">
              {dayjs(checkOutDate).format(textConstants.MMDDYYYY_FORMAT)}
            </Typography>
          </Box>

          <Divider className="line-item gutter-bottom" />
          <Typography variant="body1" className="bold section-title">
            {textConstants.TOTAL_PER_TRAVELER}:
          </Typography>
          {pricingBreakdown.savingsAmount?.fiat.value &&
            pricingBreakdown.savingsAmount?.fiat.value < 0 ? (
            <Box className="line-item">
              <Typography variant="body2" className="green">
                {textConstants.PACKAGE_SAVINGS}:
              </Typography>
              <Typography variant="body2" className="bold green">
                {getTotalPriceText({
                  price: {
                    ...pricingBreakdown.savingsAmount?.fiat,
                    value:
                      pricingBreakdown.savingsAmount?.fiat.value /
                      pricingBreakdown.subtotal.numTravelers,
                  },
                  priceFormatter: twoDecimalFormatter,
                })}
              </Typography>
            </Box>
          ) : undefined}
          <Box className="line-item gutter-bottom">
            <Typography variant="body1">
              {textConstants.PER_TRAVELER}:
            </Typography>
            <Typography variant="body1" className="bold">
              {addCurrency(
                (pricingBreakdown.subtotal.totalAmount.fiat.value +
                  (pricingBreakdown.savingsAmount?.fiat.value ?? 0)) /
                pricingBreakdown.subtotal.numTravelers
              )}
            </Typography>
          </Box>
          {pricingBreakdown.seatsAmount && (
            <>
              <Divider className="line-item gutter-bottom" />
              <Typography variant="body1" className="bold section-title">
                {textConstants.ANCILLARIES}:
              </Typography>

              <Box className="line-item gutter-bottom">
                <Typography variant="body1">
                  {textConstants.SEAT_SELECTION}:
                </Typography>
                <Typography variant="body1" className="bold">
                  {getTotalPriceText({
                    price: pricingBreakdown.seatsAmount?.fiat,
                    priceFormatter: twoDecimalFormatter,
                  })}
                </Typography>
              </Box>
            </>
          )}
          <Divider className="line-item gutter-bottom" />
          <Box
            className={clsx("line-item", {
              "gutter-bottom": !pricingBreakdown.payAtHotelAmount,
            })}
          >
            <Typography variant="body1" className="bold">
              {textConstants.getPackageTotal(
                pricingBreakdown.subtotal.numTravelers,
                isCorporate
              )}
              :
            </Typography>
            <Typography variant="body1" className="bold">
              {addCurrency(pricingBreakdown.payNowAmount.fiat.value)}
            </Typography>
          </Box>
          {pricingBreakdown.payAtHotelAmount?.fiat.value && (
            <Box className="line-item">
              <Typography variant="body1" className="gutter-bottom bold">
                {textConstants.ADDITIONAL_FEES}:
              </Typography>
              <Typography variant="body1" className="gutter-bottom bold">
                {addCurrency(pricingBreakdown.payAtHotelAmount?.fiat.value)}
              </Typography>
            </Box>
          )}
          <Typography variant="caption" className="line-item gutter-bottom">
            {textConstants.COPY1(isCorpTenant(config.TENANT))}
          </Typography>
          <Divider className="line-item gutter-bottom" />

          <Box className="gutter-bottom">
            <Typography variant="body1" className="bold section-title">
              {textConstants.PAYMENT_INFO}
            </Typography>

            {renderOffers}
            {renderCredits}
            {renderRewards}
            {renderPayment}
            {renderEarnBanner}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
